import React, { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiUser, FiMail, FiShield, FiLock } from 'react-icons/fi';
import api from '../services/api';

const EditUser = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        cpf: '',
        matricula: '',
        profile_id: ''
    });
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState({ type: '', msg: '' });
    const [validated, setValidated] = useState(false);

    useEffect(() => {
        fetchProfiles();
        fetchUser();
    }, [id]);

    const fetchProfiles = async () => {
        try {
            const response = await api.get('/profiles');
            setProfiles(response.data);
        } catch (error) {
            console.error('Erro ao carregar perfis', error);
            // Não bloqueia a edição se falhar, mas o select ficará vazio ou com opções padrão se tivéssemos fallback
        }
    };

    const fetchUser = async () => {
        try {
            const res = await api.get(`/users/${id}`);
            setFormData({
                nome: res.data.nome,
                email: res.data.email,
                cpf: res.data.cpf,
                matricula: res.data.matricula,
                profile_id: res.data.profile_id || '' // Prioriza profile_id
            });
            
            // Fallback para perfil legado se profile_id não vier (embora o backend agora retorne)
            if (!res.data.profile_id && res.data.perfil) {
                // Se não tiver profile_id mas tiver perfil string, tentamos achar o ID correspondente depois que profiles carregar
                // Mas como é assíncrono, talvez seja melhor confiar no backend retornando profile_id
            }
        } catch (err) {
            setFeedback({ type: 'danger', msg: 'Erro ao carregar dados do usuário.' });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
        
        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(true);
        setLoading(true);
        setFeedback({ type: '', msg: '' });

        try {
            // Envia profile_id e também o nome do perfil para compatibilidade
            const selectedProfile = profiles.find(p => p.id === parseInt(formData.profile_id));
            const payload = {
                ...formData,
                perfil: selectedProfile ? selectedProfile.nome : ''
            };

            await api.put(`/users/${id}`, payload);
            setFeedback({ type: 'success', msg: 'Usuário atualizado com sucesso! Redirecionando...' });
            setTimeout(() => navigate('/user-management'), 2000);
        } catch (err) {
            const errorMsg = err.response?.data?.msg || 'Erro ao atualizar usuário. Tente novamente.';
            setFeedback({ type: 'danger', msg: errorMsg });
            setLoading(false);
        }
    };

    return (
        <Container className="py-5" style={{ maxWidth: '800px' }}>
            <div className="mb-4 d-flex align-items-center justify-content-between">
                <div>
                    <Button variant="link" 
                        as={Link} 
                        to="/user-management" 
                        className="text-decoration-none p-0 mb-1 text-muted">
                        <FiArrowLeft /> Voltar para Gestão de usuários
                    </Button>
                </div>                
            </div>
            <div>
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>Editar Usuário</h2>
            </div>

            <Card className="shadow-sm border-0 rounded-4">
                <Card.Body className="p-4 p-md-5">
                    {feedback.msg && <Alert variant={feedback.type} dismissible onClose={() => setFeedback({ type: '', msg: '' })}>{feedback.msg}</Alert>}
                    
                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Row className="g-4">
                            <Col md={12}>
                                <h5 className="mb-3 text-primary d-flex align-items-center gap-2">
                                    <FiUser /> Informações Pessoais
                                </h5>
                            </Col>
                            
                            <Col md={12}>
                                <Form.Group controlId="nome">
                                    <Form.Label>Nome Completo</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        placeholder="Ex: João da Silva"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe o nome completo.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="cpf">
                                    <Form.Label>CPF</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="cpf"
                                        value={formData.cpf}
                                        onChange={handleChange}
                                        placeholder="000.000.000-00"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe o CPF.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="matricula">
                                    <Form.Label>Matrícula</Form.Label>
                                    <Form.Control 
                                        type="text" 
                                        name="matricula"
                                        value={formData.matricula}
                                        onChange={handleChange}
                                        placeholder="Ex: 123456"
                                        required 
                                        className="py-2"
                                    />
                                    <Form.Control.Feedback type="invalid">Por favor, informe a matrícula.</Form.Control.Feedback>
                                </Form.Group>
                            </Col>

                            <Col md={12}>
                                <hr className="my-2" />
                            </Col>

                            <Col md={12}>
                                <h5 className="mb-3 text-primary d-flex align-items-center gap-2">
                                    <FiMail /> Contato e Acesso
                                </h5>
                            </Col>

                            <Col md={12}>
                                <Form.Group controlId="email">
                                    <Form.Label>Email</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiMail /></span>
                                        <Form.Control 
                                            type="email" 
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="exemplo@email.com"
                                            required 
                                            className="py-2 border-start-0"
                                        />
                                        <Form.Control.Feedback type="invalid">Por favor, informe um email válido.</Form.Control.Feedback>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group controlId="profile_id">
                                    <Form.Label>Perfil de Acesso</Form.Label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0"><FiShield /></span>
                                        <Form.Select 
                                            name="profile_id"
                                            value={formData.profile_id}
                                            onChange={handleChange}
                                            className="py-2 border-start-0"
                                            required
                                        >
                                            <option value="">Selecione...</option>
                                            {profiles.map(profile => (
                                                <option key={profile.id} value={profile.id}>
                                                    {profile.nome}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">Selecione um perfil de acesso.</Form.Control.Feedback>
                                    </div>
                                </Form.Group>
                            </Col>

                            <Col md={12} className="mt-4 d-flex justify-content-end gap-3">
                                <Button variant="outline-secondary" as={Link} to="/user-management" className="px-4">
                                    Cancelar
                                </Button>
                                <Button variant="primary" type="submit" disabled={loading} className="px-4 d-flex align-items-center gap-2">
                                    {loading ? <Spinner animation="border" size="sm" /> : <FiSave />}
                                    Salvar Alterações
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default EditUser;
